import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Alert, Button, Spinner } from "@rekonas/ui";
import { ApiError, authCacheScope, authDataIdentity, subscribeAuthDataIdentity,
  createSummaryEmail, previewSummaryEmail, getSummaryEmail, listSummaryEmails, summaryEmailKeys,
  type SummaryEmailJob, type SummaryEmailPreview, type SummaryEmailRequest } from "@rekonas/data-access";
import { formatRecordingDuration } from "../recordingLabel";

const active = (job?: SummaryEmailJob) => !!job && ["queued", "preparing", "sending"].includes(job.state);
const denied = (error: unknown) => error instanceof ApiError && [401, 403, 404].includes(error.status);
const pendingKey = () => `neoma.summary-request.v1:${authCacheScope()}`;
function readPending(): SummaryEmailRequest | null {
  try {
    const value = JSON.parse(sessionStorage.getItem(pendingKey()) || "null") as SummaryEmailRequest | null;
    return value && Array.isArray(value.recording_ids) && typeof value.client_request_id === "string" && typeof value.preview_revision === "string" ? value : null;
  } catch { return null; }
}
function writePending(value: SummaryEmailRequest | null) {
  if (value) sessionStorage.setItem(pendingKey(), JSON.stringify(value));
  else sessionStorage.removeItem(pendingKey());
  window.dispatchEvent(new Event("neoma-summary-request"));
}

export function summaryError(error: unknown): string {
  const code = error instanceof ApiError ? (error.details as {error?: {code?: string}})?.error?.code : "";
  const messages: Record<string, string> = {
    summary_disabled: "Summary email is currently disabled.", mail_unconfigured: "Email delivery is not configured. Please contact your administrator.",
    summary_review_changed: "Your account email or saved results changed. Close this review and review the selection again.",
    summary_recipient_forbidden: "You do not have permission to send summaries to another address.",
    summary_email_address_invalid: "Enter a single valid email address.",
    summary_no_saved_data: "None of these recordings has usable saved metrics or stages.",
    summary_active_limit: "Another summary export is active. Check Recent exports before sending again.",
    summary_daily_limit: "The daily summary limit has been reached. Try after midnight UTC.",
    summary_batch_too_large: "This batch exceeds the export limits. Select fewer recordings.",
    summary_email_too_large: "The attachments exceed the email limit. Select fewer recordings.",
    summary_batch_limit: "Too many recordings are selected. Select fewer recordings.",
    account_email_changed: "Your account email changed. Review and submit a new batch.",
    account_verification_required: "Your account email needs verification. See Account settings.",
    summary_source_unavailable: "A reviewed saved result is no longer available. Review the selection again.",
    summary_access_revoked: "Recording export access is no longer available.",
    summary_generation_failed: "The attachments could not be prepared. Contact your administrator before trying again.",
    generation_attempts_exhausted: "Attachment preparation stopped after repeated interruptions. Contact your administrator.",
    mail_not_delivered: "The mail server did not accept the email after the allowed retries. Contact your administrator.",
    account_authentication_disabled: "Account authentication is disabled. Contact your administrator.",
    account_policy_unsupported: "Summary email cannot validate this account policy. Contact your administrator.",
  };
  return messages[code || ""] || (denied(error) ? "Export access is no longer available. Protected details are hidden." :
    "The request could not be confirmed. Check Recent exports; retrying recovery uses the same request identity.");
}

export function SummaryJobStatus({job}: {job: SummaryEmailJob}) {
  const labels: Record<SummaryEmailJob["state"], string> = {
    queued: "Queued for preparation", preparing: "Preparing PDF and spreadsheet", sending: "Sending email…",
    accepted: "Email sent.",
    failed: "Email could not be sent.",
    delivery_uncertain: "We could not confirm whether the email was sent. Contact your administrator before sending another copy.",
  };
  const reason = job.state === "failed" ? summaryError(new ApiError("", 409, {error: {code: job.code}})) : "";
  return <div className="summary-job-status" role="status"><strong>{labels[job.state]}</strong>{reason && <p>{reason}</p>}</div>;
}

export function SummaryEmailAction({ids, onCreated}: {ids: string[]; onCreated?: () => void}) {
  const identity = useSyncExternalStore(subscribeAuthDataIdentity, authDataIdentity, authDataIdentity);
  return <SummaryEmailActionScoped key={identity + ids.join(",")} ids={ids} onCreated={onCreated} />;
}
function SummaryEmailActionScoped({ids, onCreated}: {ids: string[]; onCreated?: () => void}) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState<SummaryEmailPreview | null>(null);
  const [recipient, setRecipient] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<SummaryEmailJob | null>(null);
  const request = useRef<SummaryEmailRequest | null>(null);
  const inFlight = useRef(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const recipientInput = useRef<HTMLInputElement>(null);
  const client = useQueryClient();
  const status = useQuery({queryKey: summaryEmailKeys.job(submitted?.job_id || ""), enabled: !!submitted,
    queryFn: ({signal}) => getSummaryEmail(submitted!.job_id, signal), retry: false,
    refetchInterval: query => active(query.state.data || submitted || undefined) ? 2000 : false});
  useEffect(() => { if (open) dialog.current?.showModal(); else dialog.current?.close(); }, [open]);
  async function review() {
    setOpen(true); setLoading(true); setError(null); setPreview(null); setSubmitted(null); request.current = null;
    try {
      const reviewed = await previewSummaryEmail(ids);
      setPreview(reviewed); setRecipient(reviewed.recipient);
    }
    catch (cause) { setError(cause); }
    finally { setLoading(false); }
  }
  const recipientChanged = !!preview && recipient.trim() !== preview.recipient;
  async function reviewRecipient() {
    if (loading || submitting || submitted || !recipientInput.current?.reportValidity()) return;
    setLoading(true); setError(null); request.current = null;
    try {
      const reviewed = await previewSummaryEmail(ids, undefined, recipient.trim());
      setPreview(reviewed); setRecipient(reviewed.recipient);
    } catch (cause) {
      setError(cause); setPreview(null);
    } finally { setLoading(false); }
  }
  async function send() {
    if (!preview?.sendable || recipientChanged || loading || inFlight.current || submitted || readPending()) return;
    inFlight.current = true; setSubmitting(true); setError(null);
    request.current ||= {recording_ids: ids, client_request_id: crypto.randomUUID(), preview_revision: preview.preview_revision,
      ...(preview.can_override_recipient ? {recipient: preview.recipient} : {})};
    try {
      // Persist identity before the network call. A lost response is recovered
      // explicitly from Recent exports, never by creating a new request ID.
      writePending(request.current);
      const job = await createSummaryEmail(request.current);
      writePending(null); setSubmitted(job);
      await client.invalidateQueries({queryKey: summaryEmailKeys.all()});
      onCreated?.();
    } catch (cause) {
      if (cause instanceof ApiError && cause.status < 500) writePending(null);
      setError(cause);
      if (denied(cause)) setPreview(null);
    } finally { inFlight.current = false; setSubmitting(false); }
  }
  return <>
    <Button size="sm" variant="secondary" disabled={!ids.length || ids.length > 10} onClick={() => void review()}>Email summaries</Button>
    <dialog ref={dialog} className="summary-email-dialog" aria-labelledby="summary-review-title" onCancel={() => setOpen(false)} onClose={() => setOpen(false)}>
      <header className="summary-dialog-heading"><h2 id="summary-review-title">Review recording summaries</h2><p>Review the recipient and saved results before sending.</p></header>
      {loading && <p role="status"><Spinner /> Checking saved results…</p>}
      {error != null && <Alert>{summaryError(error)}</Alert>}
      {status.isError && <Alert>{denied(status.error) ? "Export access is no longer available. Protected details are hidden." : "Status could not be refreshed. The submitted export is retained; no email was resubmitted."}
        <Button size="sm" variant="ghost" onClick={() => void status.refetch()}>Retry status</Button></Alert>}
      {preview && !denied(error) && !denied(status.error) && <>
        <section className="summary-recipient-card" aria-label="Email recipient"><h3>{preview.can_override_recipient ? "Send to" : "Send to your account address"}</h3>
        {preview.can_override_recipient && !submitted ? <label className="summary-recipient-field">Destination email
          <input ref={recipientInput} type="email" required maxLength={254} value={recipient} disabled={loading || submitting}
            onChange={event => { setRecipient(event.target.value); setError(null); request.current = null; }} />
        </label> : <strong className="summary-recipient">{preview.recipient}</strong>}
        <Link to="/account/settings" onClick={() => setOpen(false)}>Account settings</Link></section>
        <h3 className="summary-section-title">Selected recordings <span>({preview.recordings.length})</span></h3>
        <ul className="summary-review-recordings">{preview.recordings.map(r => <li key={r.recording_id}>
          <h4>{r.label}</h4><p className="summary-recording-date">{r.date}</p><small>{r.recording_id}</small>
          <p>{formatRecordingDuration(r.duration_seconds)} · Metrics {r.metrics_available ? "available" : "unavailable"} · Hypnogram {r.stages_available ? "available" : "unavailable"}</p>
          {r.notices.map((notice, i) => <p key={i} className="summary-notice">{notice}</p>)}
        </li>)}</ul>
        <section className="summary-attachments" aria-label="Email attachments"><h3>Included attachments</h3><p>One PDF and one OpenDocument spreadsheet (ODS) cover all selected recordings. Missing data will be labelled. No new analysis will run.</p></section>
        {!preview.sendable && <Alert>None of the selected recordings has usable saved metrics or stages. This batch cannot be sent.</Alert>}
      </>}
      {submitted && !denied(status.error) && <SummaryJobStatus job={status.data || submitted} />}
      <div className="summary-dialog-actions">
        <Button variant="secondary" onClick={() => setOpen(false)}>Close</Button>
        <Button onClick={() => void (recipientChanged ? reviewRecipient() : send())} disabled={!preview?.sendable || loading || submitting || !!submitted || denied(error) || !!readPending()}>
          {submitting ? "Submitting…" : recipientChanged ? "Review destination" : "Send PDF and spreadsheet"}
        </Button>
      </div>
      {readPending() && !submitting && <p>An unconfirmed request is saved. Close this dialog and use Recent exports to recover it before sending another.</p>}
    </dialog>
  </>;
}

export function RecentSummaryEmails() {
  const identity = useSyncExternalStore(subscribeAuthDataIdentity, authDataIdentity, authDataIdentity);
  return <RecentSummaryEmailsScoped key={identity} />;
}
function RecentSummaryEmailsScoped() {
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const [selected, setSelected] = useState("");
  const [pending, setPending] = useState(readPending);
  const [recovering, setRecovering] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const inFlight = useRef(false);
  const client = useQueryClient();
  useEffect(() => {
    const changed = () => setPending(readPending());
    window.addEventListener("neoma-summary-request", changed);
    return () => window.removeEventListener("neoma-summary-request", changed);
  }, []);
  const history = useQuery({queryKey: summaryEmailKeys.history(cursor), enabled: open,
    queryFn: ({signal}) => listSummaryEmails(cursor, signal), retry: false,
    refetchInterval: query => query.state.data?.items.some(active) ? 3000 : false});
  const status = useQuery({queryKey: summaryEmailKeys.job(selected), enabled: open && !!selected,
    queryFn: ({signal}) => getSummaryEmail(selected, signal), retry: false,
    refetchInterval: query => active(query.state.data) ? 2000 : false});
  async function recover() {
    if (!pending || inFlight.current) return;
    inFlight.current = true; setRecovering(true); setError(null);
    try {
      const job = await createSummaryEmail(pending);
      writePending(null); setSelected(job.job_id); setOpen(true);
      await client.invalidateQueries({queryKey: summaryEmailKeys.all()});
    } catch (cause) {
      setError(cause);
      if (cause instanceof ApiError && cause.status < 500) writePending(null);
    } finally { inFlight.current = false; setRecovering(false); }
  }
  const hidden = denied(history.error) || denied(status.error) || denied(error);
  return <section className="summary-recent" aria-label="Recent summary exports">
    <Button size="sm" variant="ghost" aria-expanded={open} onClick={() => setOpen(!open)}>Recent exports</Button>
    {pending && !hidden && <div><p>A summary submission has an unconfirmed response. Recovery reuses that request; it does not create a duplicate batch.</p>
      <Button size="sm" disabled={recovering} onClick={() => void recover()}>{recovering ? "Checking…" : "Recover saved request"}</Button></div>}
    {error != null && <Alert>{summaryError(error)}</Alert>}
    {open && <>
      {(history.isError || status.isError) && <Alert>{hidden ? "Export access is no longer available. Protected details are hidden." : "Status could not be refreshed. Your selected export is retained; no email was resubmitted."}
        <Button size="sm" variant="ghost" onClick={() => { void history.refetch(); if (selected) void status.refetch(); }}>Retry status</Button></Alert>}
      {!hidden && <>
        {history.isPending && <p role="status">Loading recent exports…</p>}
        {history.data?.items.length === 0 && <p>No recent exports.</p>}
        <ul>{history.data?.items.map(job => <li key={job.job_id}>
          <Button size="sm" variant="ghost" aria-pressed={selected === job.job_id} onClick={() => setSelected(job.job_id)}>
            {job.recording_count} recording{job.recording_count === 1 ? "" : "s"} · {job.created} · {job.state === "accepted" ? "Email sent" : job.state.replaceAll("_", " ")}
          </Button>
        </li>)}</ul>
        {status.data && <SummaryJobStatus job={status.data} />}
        <div className="summary-dialog-actions"><Button size="sm" variant="ghost" disabled={!cursor} onClick={() => setCursor(null)}>Newest</Button>
          <Button size="sm" variant="ghost" disabled={!history.data?.next_cursor} onClick={() => setCursor(history.data?.next_cursor || null)}>Older exports</Button></div>
      </>}
    </>}
  </section>;
}
